import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { fetchAssemblyGroups, type AssemblyGroup } from '../lib/api';

const fmtNum = (n: number | null | undefined) =>
  n != null ? Math.round(Number(n)).toLocaleString() : '\u2014';

const fmtAcres = (n: number | null | undefined) =>
  n != null ? `${Math.round(Number(n)).toLocaleString()} ac` : '\u2014';

// Acreage tier classification — mirrors map layer 4-tier coloring
function getAcreageTier(acres: number): { label: string; color: string } {
  if (acres >= 10000) return { label: 'MEGA', color: '#16a34a' };     // dcPrime green
  if (acres >= 2500)  return { label: 'BTM SCALE', color: '#2563eb' }; // dcStrong blue
  if (acres >= 1000)  return { label: 'MODERATE', color: '#ca8a04' };  // dcViable yellow
  return { label: 'SMALL', color: '#6A7E8E' };                        // dcWeak dim
}

interface Knockouts {
  minAcreage: number | null;
  minParcels: number | null;
}

const DEFAULT_KNOCKOUTS: Knockouts = { minAcreage: null, minParcels: null };
const STORAGE_KEY = 'pp-assembly-settings';

function loadSettings(): { knockouts: Knockouts } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const { minAcreage, minParcels } = { ...DEFAULT_KNOCKOUTS, ...parsed.knockouts };
      return { knockouts: { minAcreage, minParcels } };
    }
  } catch { /* noop */ }
  return { knockouts: DEFAULT_KNOCKOUTS };
}

function saveSettings(knockouts: Knockouts) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ knockouts })); } catch { /* noop */ }
}

type SortField = 'acreage' | 'parcels' | 'owner' | 'systems' | 'nearest';

const anyKnockoutActive = (k: Knockouts) => k.minAcreage != null || k.minParcels != null;

const printStyles = `
@media print {
  .assemblies-toolbar { display: none !important; }
  @page { margin: 0.4in; size: letter landscape; }
}
`;

export const AssembliesReport: React.FC<{
  onSelectAssembly?: (id: string) => void;
}> = ({ onSelectAssembly }) => {
  const [assemblies, setAssemblies] = useState<AssemblyGroup[] | null>(null);
  const [serverTotal, setServerTotal] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState<string>('TX');
  const [knockouts, setKnockouts] = useState<Knockouts>(() => loadSettings().knockouts);
  const [sortField, setSortField] = useState<SortField>('acreage');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { theme, isDark } = useTheme();

  /* Theme-aware panel colors — shadows PrintAtoms.REPORT for dark mode */
  const REPORT = useMemo(() => ({
    heading: theme.text,
    body: theme.textSecondary,
    secondary: theme.textSecondary,
    tertiary: theme.textTertiary,
    rule: theme.borderLight,
    surface: theme.surface,
    border: theme.border,
    white: theme.background,
    accent: isDark ? '#D49880' : '#C4705A',
    highlight: isDark ? '#8A9CAA' : '#506272',
    fail: '#A04030',
  }), [theme, isDark]);

  useEffect(() => { saveSettings(knockouts); }, [knockouts]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setAssemblies(null);
    fetchAssemblyGroups({ state: stateFilter || undefined, limit: 1000 })
      .then(data => { if (!cancelled) { setAssemblies(data.assemblies); setServerTotal(data.total || 0); } })
      .catch(() => { if (!cancelled) setError('Failed to load assembly groups'); });
    return () => { cancelled = true; };
  }, [stateFilter]);

  const ranked = useMemo(() => {
    if (!assemblies) return [];

    const filtered = assemblies.map((a, _i) => ({
      ...a,
      knockedOut:
        (knockouts.minAcreage != null && Number(a.total_acreage) < knockouts.minAcreage) ||
        (knockouts.minParcels != null && a.parcel_count < knockouts.minParcels),
      rank: 0,
    }));

    const sortFn = (a: typeof filtered[0], b: typeof filtered[0]) => {
      let va: number | string, vb: number | string;
      switch (sortField) {
        case 'acreage': va = Number(a.total_acreage); vb = Number(b.total_acreage); break;
        case 'parcels': va = a.parcel_count; vb = b.parcel_count; break;
        case 'owner': va = (a.owner_name || '').toLowerCase(); vb = (b.owner_name || '').toLowerCase(); break;
        case 'systems': va = Number(a.pipeline_system_count || 0); vb = Number(b.pipeline_system_count || 0); break;
        case 'nearest': va = Number(a.nearest_pipeline_miles ?? 999); vb = Number(b.nearest_pipeline_miles ?? 999); break;
        default: va = Number(a.total_acreage); vb = Number(b.total_acreage);
      }
      if (typeof va === 'string') return sortDir === 'desc' ? String(vb).localeCompare(va) : va.localeCompare(String(vb));
      return sortDir === 'desc' ? (vb as number) - (va as number) : (va as number) - (vb as number);
    };

    const active = filtered.filter(c => !c.knockedOut).sort(sortFn);
    const knockedOut = filtered.filter(c => c.knockedOut).sort(sortFn);
    active.forEach((c, i) => { c.rank = i + 1; });
    return [...active, ...knockedOut];
  }, [assemblies, knockouts, sortField, sortDir]);

  const handleSort = useCallback((field: SortField) => {
    if (field === sortField) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  }, [sortField]);

  const updateKnockout = useCallback(<K extends keyof Knockouts>(key: K, value: Knockouts[K]) => {
    setKnockouts(k => ({ ...k, [key]: value }));
  }, []);

  const activeCount = ranked.filter(c => !c.knockedOut).length;
  const totalCount = serverTotal || ranked.length;

  // KPI aggregates over active (non-knocked-out) assemblies
  const kpi = useMemo(() => {
    const active = ranked.filter(c => !c.knockedOut);
    return {
      totalAcres: active.reduce((s, a) => s + Number(a.total_acreage), 0),
      totalParcels: active.reduce((s, a) => s + a.parcel_count, 0),
      megaCount: active.filter(a => Number(a.total_acreage) >= 10000).length,
      btmCount: active.filter(a => Number(a.total_acreage) >= 2500).length,
      withPipeline: active.filter(a => Number(a.pipeline_system_count || 0) > 0).length,
    };
  }, [ranked]);

  const sortArrow = (field: SortField) =>
    field !== sortField ? '' : sortDir === 'desc' ? ' \u25BE' : ' \u25B4';

  const TH: React.CSSProperties = {
    padding: '6px 10px', fontWeight: 600, fontSize: '8px',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    color: REPORT.secondary, background: REPORT.surface,
    fontFamily: 'monospace', cursor: 'pointer', userSelect: 'none',
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', flex: 1, width: '100%', overflow: 'hidden',
      fontFamily: '"DM Sans", -apple-system, sans-serif',
      background: REPORT.white, color: REPORT.heading,
    }}>
      <style>{printStyles}</style>

      {/* Sub-toolbar */}
      <div className="assemblies-toolbar" style={{
        height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', background: '#2E3840', borderBottom: '1px solid rgba(80,98,114,0.10)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <select
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
            style={{
              padding: '3px 8px', fontSize: '11px', fontFamily: '"DM Sans", sans-serif',
              background: '#3A4650', color: '#E4E6E8', border: '1px solid rgba(80,98,114,0.3)',
              borderRadius: '3px', cursor: 'pointer',
            }}
          >
            <option value="">All States</option>
            <option value="TX">Texas</option>
            <option value="OK">Oklahoma</option>
            <option value="NM">New Mexico</option>
            <option value="AZ">Arizona</option>
          </select>
          {assemblies && (
            <span style={{ fontSize: '11px', color: '#8A9CAA' }}>
              {activeCount} of {totalCount} assemblies{anyKnockoutActive(knockouts) ? ' (filtered)' : ''}
            </span>
          )}
        </div>
        <button onClick={() => window.print()} style={{
          padding: '4px 14px', background: REPORT.accent, color: '#E4E6E8',
          border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: 500,
          cursor: 'pointer', fontFamily: '"DM Sans", sans-serif',
        }}>
          Export
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 28px 64px' }}>

          {!assemblies && !error && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: REPORT.secondary }}>
              Loading assembly groups...
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: REPORT.fail }}>{error}</div>
          )}

          {assemblies && (
            <>
              {/* Header */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  fontSize: '9px', fontWeight: 500, color: REPORT.accent,
                  textTransform: 'uppercase', letterSpacing: '0.12em',
                  fontFamily: 'monospace',
                }}>
                  Land Assembly Intelligence
                </div>
                <div style={{ fontSize: '22px', fontWeight: 400, color: REPORT.heading, fontFamily: 'Georgia, serif', lineHeight: 1.2 }}>
                  Assembly Group Rankings
                </div>
                <div style={{ fontSize: '11px', color: REPORT.secondary, marginTop: '4px' }}>
                  {stateFilter || 'All States'} &middot; Owner-clustered contiguous parcels &middot; Ranked by assemblable acreage
                </div>
              </div>

              {/* KPI cards — briefing grade-card style */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '24px' }}>
                <KPICard label="Assemblies" value={fmtNum(totalCount)} color={REPORT.highlight} />
                <KPICard label="BTM Scale (2,500+ ac)" value={fmtNum(kpi.btmCount)} color="#2563eb" />
                <KPICard label="Mega (10K+ ac)" value={fmtNum(kpi.megaCount)} color="#16a34a" />
                <KPICard label="Pipeline Access" value={fmtNum(kpi.withPipeline)} color="#7c3aed" />
                <KPICard label="Pipeline Systems" value={fmtNum(kpi.withPipeline)} color={REPORT.accent} />
              </div>
              <p style={{ fontSize: '10px', color: REPORT.secondary, marginTop: '-14px', marginBottom: '24px' }}>Pipeline system counts and distance are physical context; native delivery capacity and firm transportation remain unconfirmed.</p>

              {/* Knockout filters */}
              <div style={{
                background: REPORT.surface, borderRadius: '6px', padding: '12px 16px',
                marginBottom: '24px',
              }}>
                <div style={{
                  fontSize: '9px', fontWeight: 600, color: REPORT.accent,
                  textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px',
                  fontFamily: 'monospace',
                }}>
                  Filters
                </div>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <ChipRow label="Min Acreage" chips={[500, 1000, 2500, 10000]} format={v => v >= 1000 ? `${v / 1000}K` : `${v}`}
                    active={knockouts.minAcreage} onSelect={v => updateKnockout('minAcreage', v)} />
                  <ChipRow label="Min Parcels" chips={[3, 5, 10, 20]} format={v => `${v}+`}
                    active={knockouts.minParcels} onSelect={v => updateKnockout('minParcels', v)} />
                  {anyKnockoutActive(knockouts) && (
                    <button
                      onClick={() => setKnockouts(DEFAULT_KNOCKOUTS)}
                      style={{
                        padding: '3px 10px', fontSize: '9px',
                        background: 'none', border: `1px solid ${REPORT.border}`, borderRadius: '3px',
                        color: REPORT.secondary, cursor: 'pointer', fontFamily: 'monospace',
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Rankings section */}
              <div style={{
                fontSize: '10px', fontWeight: 700, color: REPORT.heading,
                textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px',
                borderBottom: `2px solid ${REPORT.highlight}`, paddingBottom: '4px',
              }}>
                Assembly Rankings
              </div>

              <div style={{ background: isDark ? REPORT.surface : 'white', borderRadius: '6px', overflow: 'hidden', border: `1px solid ${REPORT.rule}`, marginTop: '8px' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontFamily: '"DM Sans", sans-serif', minWidth: '800px' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${REPORT.rule}` }}>
                        <th style={{ ...TH, textAlign: 'center', width: '36px', cursor: 'default' }}>#</th>
                        <th style={{ ...TH, cursor: 'default' }}>Owner</th>
                        <th style={{ ...TH, textAlign: 'center', width: '76px' }}>Tier</th>
                        <th onClick={() => handleSort('acreage')} style={{ ...TH, textAlign: 'right' }}>Acreage{sortArrow('acreage')}</th>
                        <th onClick={() => handleSort('parcels')} style={{ ...TH, textAlign: 'center' }}>Parcels{sortArrow('parcels')}</th>
                        <th onClick={() => handleSort('systems')} style={{ ...TH, textAlign: 'center' }}>Systems{sortArrow('systems')}</th>
                        <th onClick={() => handleSort('nearest')} style={{ ...TH, textAlign: 'right' }}>Nearest{sortArrow('nearest')}</th>
                        <th style={{ ...TH, cursor: 'default' }}>Counties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranked.map((a, i) => {
                        const tier = getAcreageTier(Number(a.total_acreage));
                        return (
                          <tr
                            key={a.id}
                            onClick={() => onSelectAssembly?.(a.id)}
                            style={{
                              borderBottom: i < ranked.length - 1 ? `1px solid ${REPORT.rule}80` : 'none',
                              background: i % 2 === 1 ? `${REPORT.surface}40` : 'transparent',
                              opacity: a.knockedOut ? 0.35 : 1,
                              cursor: onSelectAssembly ? 'pointer' : 'default',
                            }}
                          >
                            <td style={{ padding: '8px 10px', textAlign: 'center', color: REPORT.tertiary, fontFamily: '"JetBrains Mono", monospace', fontSize: '10px' }}>
                              {a.knockedOut ? '\u2014' : a.rank}
                            </td>
                            <td style={{ padding: '8px 10px' }}>
                              <div style={{ fontWeight: 600, color: REPORT.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '280px' }}>
                                {a.owner_name || 'Unknown Owner'}
                              </div>
                              <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '1px' }}>
                                {a.state}
                              </div>
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-block', padding: '2px 8px', borderRadius: '3px',
                                fontSize: '8px', fontWeight: 700, letterSpacing: '0.04em',
                                background: tier.color + '18', color: tier.color,
                                fontFamily: 'monospace',
                              }}>
                                {tier.label}
                              </span>
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, color: REPORT.heading }}>
                              {fmtAcres(a.total_acreage)}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'center', fontFamily: '"JetBrains Mono", monospace', color: REPORT.body }}>
                              {a.parcel_count}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'center', fontFamily: '"JetBrains Mono", monospace', color: REPORT.body }}>
                              {a.pipeline_system_count ?? '\u2014'}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: '"JetBrains Mono", monospace', fontSize: '10px', color: REPORT.secondary }}>
                              {a.nearest_pipeline_miles != null ? `${Number(a.nearest_pipeline_miles).toFixed(1)} mi` : '\u2014'}
                            </td>
                            <td style={{ padding: '8px 10px', color: REPORT.secondary, fontSize: '10px' }}>
                              {a.counties?.join(', ') || a.county || '\u2014'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer */}
              <div style={{
                textAlign: 'center', color: REPORT.tertiary, fontSize: '9px',
                borderTop: `1px solid ${REPORT.rule}`, paddingTop: '16px', marginTop: '32px',
                fontFamily: 'monospace',
              }}>
                Land Assembly Intelligence Report &middot; {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} &middot; Confidential
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* --- Sub-components --- */

const KPICard: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <div style={{
    background: color + '18',
    borderRadius: '6px',
    padding: '12px 14px',
  }}>
    <div style={{ fontSize: '24px', fontWeight: 400, fontFamily: 'Georgia, serif', color, lineHeight: 1 }}>
      {value}
    </div>
    <div style={{
      fontSize: '9px', fontWeight: 500, color,
      textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '4px',
      fontFamily: 'monospace',
    }}>
      {label}
    </div>
  </div>
);

const ChipRow: React.FC<{
  label: string; chips: number[]; format: (v: number) => string; active: number | null; onSelect: (v: number | null) => void;
}> = ({ label, chips, format, active, onSelect }) => {
  const { theme, isDark } = useTheme();
  const accent = isDark ? '#D49880' : '#C4705A';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ fontSize: '10px', color: theme.textSecondary, fontWeight: 500 }}>{label}</span>
      <div style={{ display: 'flex', gap: '3px' }}>
        {chips.map(v => (
          <button
            key={v}
            onClick={() => onSelect(active === v ? null : v)}
            style={{
              padding: '2px 8px', fontSize: '9px', fontFamily: 'monospace',
              background: active === v ? accent : theme.background,
              color: active === v ? '#E4E6E8' : theme.textSecondary,
              border: `1px solid ${active === v ? accent : theme.border}`,
              borderRadius: '3px', cursor: 'pointer',
            }}
          >
            {format(v)}
          </button>
        ))}
      </div>
    </div>
  );
};
